import * as React from 'react';
import {useJsonForms, withJsonFormsControlProps} from '@jsonforms/react';
import {SchemaUtils} from "../../util/SchemaUtils";
import {FormControl} from '@mui/material';
import Time from "./Time";
import {DateUtils} from "../../util/DateUtils";
import {Moment} from "moment";
import {ControlValueFactory} from "../../factory/ControlValueFactory";
import {Component} from "../../enum/Component";
import { ValidationUtil } from '../../util/ValidationUtil';
import { useMemo, useCallback } from 'react';

interface TimeControlProps {
    data: any;
    label: any;
    description?: any;
    handleChange(path: string, value: any): void;
    path: string;
    uischema: any;
    schema: any;
    errors: any;
    rootSchema: any;
    appValidated?: boolean;
}

const TimeControl = ({ errors, uischema, schema, handleChange, path, data, rootSchema, appValidated }: TimeControlProps) => {

    const jsonForms = useJsonForms();

    // Memoize expensive computations
    const enabled = useMemo(() =>
            SchemaUtils.isComponentEnabled(uischema, jsonForms.core!.data),
        [uischema, jsonForms.core!.data]
    );

    const visible = useMemo(() =>
            SchemaUtils.isComponentVisible(uischema, jsonForms.core!.data),
        [uischema, jsonForms.core!.data]
    );

    const required = useMemo(() =>
            SchemaUtils.isRequired(path, rootSchema),
        [path, rootSchema]
    );

    const value = useMemo(() => {
        const rawValue = ControlValueFactory.getControlValue(Component.Time, data, uischema.scope, jsonForms, enabled && visible);
        const momentValue = DateUtils.getMomentValue(rawValue, uischema.options.ampm);

        // Make sure we return null for invalid values instead of an invalid Moment object
        return (momentValue && momentValue.isValid()) ? momentValue : null;
    }, [data, uischema.scope, jsonForms, enabled, visible, uischema.options.ampm]);

    const handleTimeChange = useCallback((moment: Moment | null) => {
        if (!moment || !moment.isValid()) {
            handleChange(path, '');
            return;
        }

        const formattedValue = moment.format(uischema.options.ampm ? 'hh:mm A' : 'HH:mm');
        handleChange(path, formattedValue);
    }, [handleChange, path, uischema.options.ampm]);

    // Clear error if value is empty (but only if it's not a required field error)
    const sanitizedError = useMemo(() => {
        if (ValidationUtil.isEmptyValue(value) && errors) {
            // Only clear error if it's not a required field error
            const isRequiredError = typeof errors === 'string' &&
                (errors.toLowerCase().includes('required'));
            return isRequiredError ? errors : "";
        }
        return errors;
    }, [errors, value]);

    // Memoize event handlers
    const handleFocus = useCallback((ev: any) => {
        // Focus handler logic if needed
    }, []);

    const handleKeyUp = useCallback((ev: any) => {
        if(ev.target.value === ''){
            handleChange(path, '');
        }
    }, [handleChange, path]);

    const format = useMemo(() =>
            DateUtils.getTimeFormat(uischema.options.ampm),
        [uischema.options.ampm]
    );

    // Memoize the Time component props
    const timeProps = useMemo(() => ({
        variant: 'outlined' as const,
        error: sanitizedError,
        label: uischema.label,
        description: schema.description,
        value: value,
        required: required,
        onChange: handleTimeChange,
        onFocus: handleFocus,
        onKeyUp: handleKeyUp,
        ampm: uischema.options.ampm,
        minutesStep: uischema.options.minutesStep,
        format: format,
        enabled: enabled,
        visible: visible,
        appValidated: appValidated
    }), [
        sanitizedError,
        uischema.label,
        schema.description,
        value,
        required,
        handleTimeChange,
        handleFocus,
        handleKeyUp,
        uischema.options.ampm,
        uischema.options.minutesStep,
        format,
        enabled,
        visible,
        appValidated
    ]);

    return (
        <FormControl className="form-control" fullWidth={true}>
            <Time {...timeProps} />
        </FormControl>
    );
};

export default React.memo(withJsonFormsControlProps(TimeControl));
